import { AnimatePresence, motion } from "motion/react"
import { cloneElement, isValidElement, useCallback, useEffect, useRef } from "react"
import { useInspectorContext } from "~/components/design-system/pages/internal/InspectorContext"
import InspectorBackButton from "~/components/design-system/pages/internal/InspectorBackButton"
import InspectorExpandButton from "~/components/design-system/pages/internal/InspectorExpandButton"
import InspectorTabs from "~/components/design-system/pages/internal/InspectorTabs"
import { useInspectorTabBar } from "~/components/design-system/pages/internal/inspectorTab"
import { useInspectorDrilldownInternal } from "~/components/design-system/pages/internal/InspectorDrilldownContext"
import { InspectorExpansionProvider } from "~/components/design-system/pages/internal/InspectorExpansionContext"
import { useHoveredRowHotkeys } from "~/components/design-system/pages/internal/hoveredRow"
import { cn } from "~/utils/helpers"

// Stable key for the inspector's own content, so it keeps its state as levels open and close above
// it.
const ROOT_VIEW = "inspector-root"

type InspectorNavChromeProps = {
  headline?: string
  onBack?: () => void
  backTo?: string
  expandSlot?: React.ReactNode
}

const ModuleInspector = () => {
  const { inspector, isOpen, canToggle, toggle, width, nav, context, canExpand, setFooterEl } =
    useInspectorContext()
  const { stack, back, close, setContainer } = useInspectorDrilldownInternal()
  const { tabs } = useInspectorTabBar()
  const depth = stack.length
  const hasTabs = tabs.length > 0
  // Hidden while drilled in: the level covers the tab inspectors, so a tab click would do nothing
  // visible and `back()` would land on a different tab than the one the planner drilled from.
  const showTabs = hasTabs && depth === 0
  // Every level: it qualifies the figures of a drilldown as much as the root view's.
  const showContext = context != null

  // Close the drilldown when the inspector collapses/unregisters. Key off inspector presence, not
  // inspector.content identity (content can be a fresh node each render → spurious close).
  useEffect(() => {
    if (!isOpen || !inspector) close()
  }, [isOpen, inspector, close])

  // A drilldown is about the item the nav names, so it cannot outlive that item: dropping the
  // highlight unregisters the nav, and the level below would otherwise stay open showing figures
  // for a row that is no longer selected. Keyed on LOSING a nav, not on having none — an inspector
  // that never registers one (its identity rendered in place) still drills down normally.
  const hadNav = useRef(false)
  useEffect(() => {
    if (nav) hadNav.current = true
    else if (hadNav.current) {
      hadNav.current = false
      close()
    }
  }, [nav, close])

  // Here, not in the inspector's chrome: it must work while the inspector is collapsed.
  useHoveredRowHotkeys(!!inspector, canToggle ? toggle : undefined)

  if (!inspector) return null

  // Injected here, not read inside `InspectorNav`: that component registers this node, so reading
  // the stack or the width there would re-register it on every change — the writer-is-also-a-reader
  // cycle `InspectorContext` documents. This component only reads, so the cycle cannot close.
  const parentTitle = depth > 1 ? stack[depth - 2]?.title : undefined
  const registeredNav = isValidElement<InspectorNavChromeProps>(nav)
    ? cloneElement(nav, {
        headline: depth > 0 ? stack[depth - 1]?.title : undefined,
        onBack: depth > 0 ? back : undefined,
        backTo: parentTitle,
        expandSlot: canExpand ? <InspectorExpandButton /> : undefined,
      })
    : nav
  // An inspector can drill down without registering a nav (the uncontrolled `InspectorNavControls`
  // renders in place, inside the root view that a level hides). Back and expand belong to the
  // chrome either way, so they get a row of their own.
  const needsBareChrome = !nav && (depth > 0 || canExpand)
  const navRow = needsBareChrome ? (
    <div className="flex w-full items-center gap-2">
      {depth > 0 && <InspectorBackButton onBack={back} parentTitle={parentTitle} />}
      <span className="label-sm text-fg-base min-w-0 flex-1 truncate">
        {stack[depth - 1]?.title}
      </span>
      {canExpand && <InspectorExpandButton />}
    </div>
  ) : (
    registeredNav
  )

  return (
    <AnimatePresence initial={false}>
      {isOpen && (
        <motion.aside
          key="module-inspector"
          initial={{ width: 0 }}
          animate={{ width }}
          exit={{ width: 0 }}
          transition={{
            type: "spring",
            stiffness: 380,
            damping: 38,
            mass: 0.9,
            restDelta: 0.5,
          }}
          className="relative shrink-0 h-full overflow-clip [overflow-clip-margin:8px] flex flex-row"
        >
          <motion.div
            initial={{ opacity: 0, x: 16 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 16 }}
            transition={{
              opacity: { duration: 0.18, ease: [0.22, 1, 0.36, 1] },
              x: { duration: 0.32, ease: [0.22, 1, 0.36, 1] },
            }}
            style={{ width }}
            className="shrink-0 h-full py-4 flex flex-col"
          >
            {/* Pinned chrome: it stays put as views replace each other, so a planner keeps the
                item's identity and their way back while several levels deep. The gutter is on
                each child, not this box, so the tab row can span the inspector edge to edge. */}
            {(navRow || hasTabs || showContext) && (
              <div
                className={cn(
                  // mb-4 is the gap above the first card — on the chrome, not on the view below,
                  // so it goes away with the chrome instead of padding a divider that isn't there.
                  "shrink-0 flex flex-col mb-4",
                  // The tab row's border doubles as the divider, which is what puts the active
                  // indicator ON the line rather than above a second one.
                  !showTabs && "pb-4 border-b border-base",
                )}
              >
                {navRow && <div className="px-4">{navRow}</div>}
                {/* Bare: the band owns its padding; the tab row or chrome rule divides it. */}
                {showContext && context}
                {showTabs && <InspectorTabs />}
              </div>
            )}

            {/* Only the top view is shown; the ones behind it stay mounted but hidden. */}
            <div className="relative flex-1 min-h-0">
              <InspectorExpansionProvider>
                {/* `inspector.className` targets the scrolling view, where it applied before
                    drilldowns nested — consumers pass `h-full` to make a self-scrolling child
                    fill it. */}
                <View key={ROOT_VIEW} isActive={depth === 0} className={inspector.className}>
                  {inspector.content}
                </View>
                {stack.map((level, index) => (
                  <DrilldownView
                    key={level.key}
                    levelKey={level.key}
                    isActive={index === depth - 1}
                    setContainer={setContainer}
                  />
                ))}
              </InspectorExpansionProvider>
            </div>
            <div
              ref={setFooterEl}
              className="shrink-0 mt-4 pt-3 px-4 border-t border-base flex items-center gap-2 empty:hidden"
            />
          </motion.div>
        </motion.aside>
      )}
    </AnimatePresence>
  )
}

/**
 * One view in the inspector: its own content, or a drilldown level. The top one replaces what
 * was there rather than sliding over it — an inspector is a narrow column, and animating a swap
 * inside it reads as motion for its own sake.
 *
 * Views behind the top one stay mounted but hidden, so a draft typed into an editor and each
 * view's scroll position survive a round trip. `hidden` (not unmounting) is what makes that work;
 * `inert` keeps the hidden ones out of the tab order.
 *
 * A level's title is not rendered here — the pinned nav shows it as its headline.
 */
const View = ({
  isActive,
  className,
  children,
}: {
  isActive: boolean
  className?: string
  children?: React.ReactNode
}) => (
  // `hidden` resolves to `display: none`, so an inactive view takes no space and the active one
  // fills the area it shares with its siblings.
  <div
    hidden={!isActive}
    inert={!isActive}
    className={cn(
      // -ml-2 + pl-6: the box runs 8px left over the resize handle so card shadows paint into the
      // aside's clip margin instead of being sliced, with the cards still at a 16px gutter. Left
      // only: past the right edge, the clip margin counts as scrollable width and the page grows a
      // horizontal scrollbar. `w-auto` because the negative margin sets the width — `w-full`
      // would size to the parent and overflow.
      "w-auto h-full scroll-box -ml-2 pl-6 pr-4 flex flex-col gap-2 [&>*]:shrink-0",
      className,
    )}
  >
    {children}
  </div>
)

/**
 * A drilldown level's view. Its own component so the portal-target ref is a `useCallback` bound to
 * this level — an inline arrow would be a fresh ref callback each render, and React detaching and
 * reattaching it would write to the stack on every commit (an infinite update loop).
 */
const DrilldownView = ({
  levelKey,
  isActive,
  setContainer,
}: {
  levelKey: string
  isActive: boolean
  setContainer: (key: string, el: HTMLElement | null) => void
}) => {
  // Owner-rendered content lands here through a portal, so it stays live with the owner's props.
  const ref = useCallback(
    (el: HTMLElement | null) => setContainer(levelKey, el),
    [levelKey, setContainer],
  )

  return (
    <View isActive={isActive}>
      <div ref={ref} className="flex flex-col gap-2 [&>*]:shrink-0" />
    </View>
  )
}

export default ModuleInspector
